import React from "react";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { getAbsoluteUrl } from "@danishagro/shared/src/components/organisms/SiteHeader/LanguageSelector/hooks/getAbsolutePath.hook";
import { DA_Image } from "@danishagro/shared/src/components/atoms/Image/Image.component";
import { DA_FooterObject } from "../../../../interfaces/footerObject.interface";
import { DA_BasicLink } from "../../../atoms/BasicLink/BasicLink.component";
import { DA_Icon, DA_IconNames } from "../../../atoms/Icon/Icon.component";
import { DA_Accordion } from "../../../molecules/Accordion/Accordion.component";
import { _FooterLink } from "../FooterLink/FooterLink.component";
import {
    AddressType,
    TextType,
    LinkType,
    SoMeType,
} from "../../../../interfaces/footerObject.interface";
import S from "./MobileFooter.module.scss";

export interface DA_MobileFooterProps {
    data: DA_FooterObject;
    isMiniFooter?: boolean;
}

export const DA_MobileFooter = ({ data, isMiniFooter = false }: DA_MobileFooterProps) => {
    const isFooterAddress = (
        item: TextType | LinkType | AddressType | SoMeType
    ): item is AddressType => {
        return item.itemType === "footerAddress";
    };

    return (
        <footer className={cn(S.mobileFooter, isMiniFooter && S.miniFooter)}>
            {data?.columns?.map((column, index) => (
                <DA_Accordion
                    key={index}
                    className={S.accordion}
                    title={column.header}
                    showToggleIcons
                    animation
                >
                    {column?.items?.map((item, index) => {
                        if (item.itemType === "footerLinkImage") {
                            return (
                                <div key={index}>
                                    <DA_BasicLink
                                        href={item.link?.url}
                                        target={item.link?.target}
                                        title={item.link?.name}
                                    >
                                        <DA_Image
                                            src={getAbsoluteUrl(item.image?.props?.umbracoFile)}
                                            alt={item.image?.name}
                                            className={S.footerLinkImage}
                                        />
                                    </DA_BasicLink>
                                </div>
                            );
                        }
                        if (item.itemType === "paymentTypes") {
                            const iconMap: Record<string, string> = {
                                dankort: "/images/payment-options/b2c-dankort.png",
                                visa: "/images/payment-options/b2c-visa.png",
                                mastercard: "/images/payment-options/b2c-mastercard.png",
                                mobilepay: "/images/payment-options/b2c-mobilepay.png",
                            };

                            return (
                                <div key={index}>
                                    <div className={S.paymentTypes}>
                                        {item.paymenticons.map((icon, index) =>
                                            iconMap[icon] ? (
                                                <img key={index} src={iconMap[icon]} alt={icon} />
                                            ) : null
                                        )}
                                    </div>
                                    <div className={S.paymentTypesText}>{item.text}</div>
                                </div>
                            );
                        }
                        if (item?.itemType === "text") {
                            return (
                                <div key={index} className={S.footerText}>
                                    {item.title && (
                                        <span className={S.itemTitle}>{item.title}</span>
                                    )}

                                    <span dangerouslySetInnerHTML={{ __html: item.text }} />
                                </div>
                            );
                        }
                        if (isFooterAddress(item)) {
                            return (
                                <div key={index} className={S.addressInfoWrapper}>
                                    {item.company && (
                                        <span
                                            itemProp="name"
                                            className={S.addressInfo}
                                            dangerouslySetInnerHTML={{
                                                __html: item.company,
                                            }}
                                        />
                                    )}
                                    {item.address1 && (
                                        <span
                                            itemProp="streetAddress"
                                            className={S.addressInfo}
                                            dangerouslySetInnerHTML={{
                                                __html: item.address1,
                                            }}
                                        />
                                    )}
                                    {item.address2 && (
                                        <span
                                            itemProp="streetAddress"
                                            className={S.addressInfo}
                                            dangerouslySetInnerHTML={{
                                                __html: item.address2,
                                            }}
                                        />
                                    )}
                                    {item.zipCity && (
                                        <span className={S.addressInfo}>
                                            <span itemProp="postalCode">
                                                {item.zipCity.split(" ")[0]}
                                            </span>
                                            <span itemProp="addressLocality">{` ${
                                                item.zipCity.split(" ")[1]
                                            }`}</span>
                                        </span>
                                    )}
                                    {item.country && (
                                        <span itemProp="addressCountry" className={S.addressInfo}>
                                            {item.country}
                                        </span>
                                    )}
                                    {item.cvr && (
                                        <span itemProp="vatID" className={S.addressInfo}>
                                            {item.cvr}
                                        </span>
                                    )}
                                    {item.email && (
                                        <span itemProp="email" className={S.addressInfo}>
                                            {item.email}
                                        </span>
                                    )}
                                    {item.phone && (
                                        <span itemProp="telephone" className={S.addressInfo}>
                                            {item.phone}
                                        </span>
                                    )}
                                </div>
                            );
                        }
                        if (item?.itemType === "link") {
                            return <_FooterLink key={index} {...item} classname={S.footerLink} />;
                        }
                    })}
                </DA_Accordion>
            ))}

            {data?.socials?.map((social, index) => (
                <div key={index} className={cn(S.mobileFooterItem, S.noBorderBottom)}>
                    <span className={S.mobileItemTitle}>{social.header}</span>
                    <div className={S.soMeLinks}>
                        {social?.items?.map((item) => {
                            let iconName;

                            switch (item.type) {
                                case "facebook":
                                    iconName = DA_IconNames.Facebook;
                                    break;
                                case "linkedin":
                                    iconName = DA_IconNames.LinkedIn;
                                    break;
                                case "instagram":
                                    iconName = DA_IconNames.Instagram;
                                    break;
                                case "youtube":
                                    iconName = DA_IconNames.YouTube;
                                    break;
                                case "tiktok":
                                    iconName = DA_IconNames.TikTok;
                                    break;
                                case "pinterest":
                                    iconName = DA_IconNames.Pinterest;
                                    break;
                                default:
                                    iconName = DA_IconNames.Question;
                            }

                            return (
                                <DA_BasicLink
                                    href={item.href}
                                    key={item.type}
                                    className={S.soMeLink}
                                    ariaLabel={item.type}
                                    target="_blank"
                                >
                                    <DA_Icon
                                        name={iconName}
                                        className={S.soMeIcon}
                                        useSvgDimensions
                                    />
                                </DA_BasicLink>
                            );
                        })}
                    </div>
                </div>
            ))}
        </footer>
    );
};
